import React from 'react';
import { Mail, MailOpen, User, Calendar, Reply, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { Message } from '../../types/messaging';

interface MessageListProps {
  messages: Message[];
  onMessageClick: (message: Message) => void;
  onReply: (message: Message) => void;
  onDelete: (messageId: string) => void;
  onMarkAsRead: (messageId: string) => void;
  isLoading?: boolean;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  onMessageClick,
  onReply,
  onDelete,
  onMarkAsRead,
  isLoading = false
}) => {
  const getProfileTypeColor = (type: string) => {
    const colors = {
      PORTEUR: 'bg-green-100 text-green-800',
      ACCOMPAGNATEUR: 'bg-blue-100 text-blue-800',
      COORDINATEUR: 'bg-purple-100 text-purple-800',
      FINANCEUR: 'bg-orange-100 text-orange-800',
      ADMIN: 'bg-red-100 text-red-800'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getProfileTypeLabel = (type: string) => {
    const labels = {
      PORTEUR: 'Porteur',
      ACCOMPAGNATEUR: 'Accompagnateur',
      COORDINATEUR: 'Coordinateur',
      FINANCEUR: 'Financeur',
      ADMIN: 'Admin'
    };
    return labels[type as keyof typeof labels] || type;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-gray-200 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
                <div className="h-3 bg-gray-200 rounded w-full" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="text-center py-12 animate-fade-in">
        <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun message</h3>
        <p className="text-gray-500">Vous n'avez pas encore reçu de messages.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {messages.map((message) => (
        <div
          key={message.id}
          onClick={() => onMessageClick(message)}
          className={`bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md hover:scale-105 transition-all duration-200 cursor-pointer group ${
            !message.is_read ? 'border-l-4 border-l-blue-500 bg-blue-50/30' : ''
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              {/* Avatar */}
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-medium text-sm">
                  {message.sender?.full_name?.charAt(0) || message.sender?.email?.charAt(0) || '?'}
                </span>
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <h3 className={`font-semibold ${!message.is_read ? 'text-gray-900' : 'text-gray-700'} group-hover:text-blue-600 transition-colors`}>
                    {message.sender?.full_name || message.sender?.email}
                  </h3>
                  {message.sender?.profile_type && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getProfileTypeColor(message.sender.profile_type)}`}>
                      {getProfileTypeLabel(message.sender.profile_type)}
                    </span>
                  )}
                  {!message.is_read && (
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
                  )}
                </div>
                
                <h4 className={`font-medium mb-2 ${!message.is_read ? 'text-gray-900' : 'text-gray-700'} line-clamp-1`}>
                  {message.subject}
                </h4>
                
                <p className="text-gray-600 text-sm line-clamp-2 mb-2">
                  {message.content}
                </p>
                
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-3 h-3" />
                    <span>{format(new Date(message.created_at), 'dd MMM yyyy à HH:mm', { locale: fr })}</span>
                  </div>
                  {message.sender?.organization && (
                    <span>{message.sender.organization}</span>
                  )}
                  {message.parent_message_id && (
                    <span className="flex items-center space-x-1">
                      <Reply className="w-3 h-3" />
                      <span>Réponse</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {!message.is_read ? (
                <MailOpen className="w-4 h-4 text-blue-600" />
              ) : (
                <Mail className="w-4 h-4 text-gray-400" />
              )}
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onReply(message);
                }}
                className="p-1 text-gray-400 hover:text-blue-600 rounded transition-colors"
                title="Répondre"
              >
                <Reply className="w-4 h-4" />
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (!message.is_read) {
                    onMarkAsRead(message.id);
                  }
                }}
                className="p-1 text-gray-400 hover:text-green-600 rounded transition-colors"
                title={message.is_read ? 'Déjà lu' : 'Marquer comme lu'}
              >
                <MailOpen className="w-4 h-4" />
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(message.id);
                }}
                className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                title="Supprimer"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};